import { Check, Copy, RotateCcw, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/shared/ui/tooltip";

interface MessageBubbleActionsProps {
    text: string;
    align?: "left" | "right";
    /**
     * «Повторить запрос»: заполняет промпт-бар этого же чата
     * параметрами генерации и останавливается — отправляет человек. Не передан
     * (у сообщения нет воспроизводимой генерации) → кнопки нет.
     */
    onRepeat?: () => void;
}

export function MessageBubbleActions({ text, align = "right", onRepeat }: MessageBubbleActionsProps) {
    const { t } = useTranslation("chat");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(timer);
    }, [copied]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
        } catch (err) {
            console.error("clipboard write failed", err);
        }
    };

    return (
        <div
            className={cn(
                "flex items-center gap-0.5 opacity-100 transition-opacity duration-100 md:opacity-0 md:group-hover:opacity-100",
                align === "right" ? "justify-end" : "justify-start",
            )}
        >
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={handleCopy}
                        aria-label={copied ? t("messages.actions.copied") : t("messages.actions.copy")}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-200 focus-visible:ring-2 focus-visible:ring-violet-400/50 focus-visible:outline-none"
                    >
                        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                    {copied ? t("messages.actions.copied") : t("messages.actions.copy")}
                </TooltipContent>
            </Tooltip>

            {onRepeat && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            onClick={onRepeat}
                            aria-label={t("messages.actions.repeat")}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-200 focus-visible:ring-2 focus-visible:ring-violet-400/50 focus-visible:outline-none"
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                        {t("messages.actions.repeat")}
                    </TooltipContent>
                </Tooltip>
            )}

            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        disabled
                        aria-label={t("messages.actions.share")}
                        className="inline-flex h-6 w-6 cursor-not-allowed items-center justify-center rounded-md text-zinc-600 opacity-60"
                    >
                        <Share2 className="h-3.5 w-3.5" />
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                    {t("messages.actions.shareSoon")}
                </TooltipContent>
            </Tooltip>
        </div>
    );
}
